import React, { useState, useEffect, useRef } from 'react';
import { Plus, Loader2, AlertCircle, CheckCircle, X } from 'lucide-react';
import { useWebSocket } from '../context/WebSocketContext';
import { compareFiles, cancelSession, cancelSessionBeacon } from '../api/compare';
import DocumentInput from '../components/DocumentInput';
import CompareResults from '../components/CompareResults';
import LoadingState from '../components/LoadingState';
import type { DocEntry, CompareResponse } from '../types';

function renameDocumentsInOrder(docs: DocEntry[]): DocEntry[] {
  return docs.map((doc, i) => ({
    ...doc,
    name: `Документ ${i + 1}`,
  }));
}

export default function ComparePage() {
  const ws = useWebSocket();

  const [documents, setDocuments] = useState<DocEntry[]>([
    { id: 1, name: 'Документ 1', text: '', file: null },
    { id: 2, name: 'Документ 2', text: '', file: null },
  ]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<CompareResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingTime, setLoadingTime] = useState(0);
  const [phraseSeed, setPhraseSeed] = useState(0);

  const abortRef = useRef<AbortController | null>(null);

  // Loading timer
  useEffect(() => {
    if (!loading) { setLoadingTime(0); return; }
    setPhraseSeed(Math.floor(Math.random() * 10000));
    const interval = setInterval(() => setLoadingTime(prev => prev + 1), 1000);
    return () => clearInterval(interval);
  }, [loading]);

  // Cancel on unmount
  useEffect(() => {
    return () => { abortRef.current?.abort(); };
  }, []);

  // Cancel stale sessions from a previous page load (runs once on mount)
  useEffect(() => {
    const savedSessions = localStorage.getItem('activeSessions');
    if (savedSessions) {
      try {
        for (const sid of JSON.parse(savedSessions)) {
          if (sid) cancelSessionBeacon(sid);
        }
      } catch {}
      localStorage.removeItem('activeSessions');
    }
  }, []);

  // Cancel active session on page close (sessionId tracked via ref to avoid
  // re-registering listeners on every sessionId change)
  const sessionIdForUnload = useRef(ws.sessionId);
  useEffect(() => { sessionIdForUnload.current = ws.sessionId; }, [ws.sessionId]);

  useEffect(() => {
    const onUnload = () => {
      const sid = sessionIdForUnload.current;
      if (sid) cancelSessionBeacon(sid);
    };
    window.addEventListener('beforeunload', onUnload);
    window.addEventListener('unload', onUnload);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      window.removeEventListener('unload', onUnload);
    };
  }, []);

  // Chunk complete -> partial results
  useEffect(() => {
    if (!loading || !ws.lastChunkComplete) return;
    const msg = ws.lastChunkComplete;
    if (ws.sessionId && msg.session_id && msg.session_id !== ws.sessionId) return;

    const diffs = (msg.differences || []).map((d, idx) => ({
      ...d,
      _isPartial: (msg.completed || 0) < (msg.total || 0),
      _chunkIndex: idx,
    }));

    setResults({
      success: true,
      differences: diffs,
      documents_count: documents.length,
      message: (msg.completed || 0) < (msg.total || 0)
        ? `Обработано ${msg.completed} из ${msg.total} частей (порядок может измениться)`
        : `Найдено ${diffs.length} различий`,
      retries: 0,
      chunks_processed: msg.completed,
      total_chunks: msg.total,
      is_partial: (msg.completed || 0) < (msg.total || 0),
    });
  }, [ws.lastChunkComplete, loading, ws.sessionId, documents.length]);

  // Document management
  const addDocument = () => {
    if (documents.length >= 5) return;
    const newId = Math.max(...documents.map(d => d.id)) + 1;
    setDocuments(prev => renameDocumentsInOrder([...prev, { id: newId, name: '', text: '', file: null }]));
  };

  const removeDocument = (id: number) => {
    if (documents.length <= 2) return;
    setDocuments(prev => renameDocumentsInOrder(prev.filter(d => d.id !== id)));
  };

  const swapDocuments = (i1: number, i2: number) => {
    setDocuments(prev => {
      const next = [...prev];
      const tmp = { file: next[i1].file, text: next[i1].text };
      next[i1] = { ...next[i1], file: next[i2].file, text: next[i2].text };
      next[i2] = { ...next[i2], file: tmp.file, text: tmp.text };
      return renameDocumentsInOrder(next);
    });
  };

  const handleFileChange = (id: number, file: File | null) => {
    setDocuments(prev => renameDocumentsInOrder(prev.map(d => d.id === id ? { ...d, file } : d)));
  };

  const handleTextChange = (id: number, text: string) => {
    setDocuments(prev => prev.map(d => d.id === id ? { ...d, text } : d));
  };

  const handleNameChange = (id: number, name: string) => {
    setDocuments(prev => prev.map(d => d.id === id ? { ...d, name } : d));
  };

  // Cancel
  const handleCancel = async () => {
    abortRef.current?.abort();
    abortRef.current = null;
    const sid = ws.sessionId;
    if (sid) {
      try { await cancelSession(sid); } catch {}
    }
    ws.setSessionId(null);
    localStorage.removeItem('activeSessions');
    setLoading(false);
    setError('Запрос отменён');
  };

  // Compare
  const handleCompare = async () => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    ws.setSessionId(null);
    ws.clearLastChunkComplete();
    localStorage.removeItem('activeSessions');
    setLoading(true);
    setError(null);
    setResults(null);

    try {
      const formData = new FormData();
      // Preserve the on-screen order. Files and texts travel in separate
      // payloads, so we tag each with its position in the documents list. The
      // backend rebuilds the interleaved order from these positions; otherwise
      // the positional "Документ N" labels in the results would not line up
      // with the documents shown here.
      const fileOrder: number[] = [];
      documents.forEach((doc, index) => {
        if (doc.file) {
          formData.append('files', doc.file);
          fileOrder.push(index);
        }
      });
      const texts = documents
        .map((doc, index) => ({ doc, index }))
        .filter(({ doc }) => !doc.file && doc.text.trim())
        .map(({ doc, index }) => ({ name: doc.name, text: doc.text, order: index }));
      formData.append('texts', JSON.stringify(texts));
      formData.append('file_order', JSON.stringify(fileOrder));
      const response = await compareFiles(formData, abortRef.current.signal);
      setResults(response.data);
    } catch (err: any) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      setError(err?.response?.data?.detail || err?.message || 'Произошла ошибка');
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  };

  const validDocsCount = documents.filter(d => d.file || d.text.trim()).length;
  const canCompare = validDocsCount >= 2;

  return (
    <>
      {/* Documents */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Документы для сравнения</h2>
          <button
            onClick={addDocument}
            disabled={documents.length >= 5}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-lg hover:from-pink-600 hover:to-rose-600 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition"
          >
            <Plus className="w-4 h-4" /> Добавить документ
          </button>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {documents.map((doc, index) => (
            <DocumentInput
              key={doc.id}
              doc={doc}
              index={index}
              totalDocs={documents.length}
              allDocs={documents}
              onFileChange={handleFileChange}
              onTextChange={handleTextChange}
              onRemove={removeDocument}
              onSwap={swapDocuments}
              onNameChange={handleNameChange}
              onError={setError}
            />
          ))}
        </div>
      </div>

      {/* Status */}
      <div className="mb-4 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-gray-200">
          <span className="text-sm text-gray-600">Загружено документов:</span>
          <span className={`font-bold ${validDocsCount >= 2 ? 'text-green-600' : 'text-amber-600'}`}>
            {validDocsCount} из {documents.length}
          </span>
          {validDocsCount >= 2 && <CheckCircle className="w-4 h-4 text-green-500" />}
        </div>
      </div>

      {/* Compare button */}
      <div className="mb-8 text-center">
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <button
            onClick={handleCompare}
            disabled={loading || !canCompare}
            className="px-8 py-3 text-white font-semibold rounded-xl transition shadow-lg hover:shadow-xl disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                Думаю... {loadingTime}с
              </span>
            ) : (
              'Сравнить документы'
            )}
          </button>

          {loading && (
            <button
              onClick={handleCancel}
              className="px-4 py-3 bg-gray-500 hover:bg-gray-600 text-white font-semibold rounded-xl transition shadow-lg hover:shadow-xl flex items-center gap-2"
            >
              <X className="w-5 h-5" /> Отмена
            </button>
          )}
        </div>

        {loading && <LoadingState loadingTime={loadingTime} isBackendDead={!ws.isConnected} phraseSeed={phraseSeed} />}

        {!canCompare && !loading && (
          <p className="mt-2 text-sm text-amber-600">
            {validDocsCount === 0
              ? 'Загрузите файлы или введите текст в минимум 2 документа'
              : `Нужно ещё ${2 - validDocsCount} документ(а)`}
          </p>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-red-800">Ошибка</h3>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      )}

      {/* Results */}
      {results && (
        <CompareResults
          results={results}
          onSwapAndRetry={() => {
            setResults(null);
            setDocuments(prev => renameDocumentsInOrder([...prev].reverse()));
          }}
        />
      )}
    </>
  );
}
